/**
 * useClientGoals — per-client goal query + CRUD mutations (Story 18.3 AC#8).
 *
 * The client Goals card (ClientGoalsCard) and the client-page GoalFormDialog
 * flow ALL data through this hook — no inline fetches in components.
 *
 * - Query key `["client-goals", clientId]` (enabled iff clientId), unwrapping
 *   the GET /api/clients/:id/goals `{ data: { goals } }` envelope. Mirrors the
 *   useClientIntegrations enabled-iff-clientId + `{ data }` unwrap pattern.
 * - create / update / delete mutations hit 18.3's per-client endpoints and, on
 *   success, invalidate BOTH this client key AND the tenant-wide ["goals"] key
 *   (the 18.8 Goals page) so the two surfaces never disagree (the TanStack v5
 *   invalidate-both idiom).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/apiClient";
import type { GoalListItem } from "../lib/goalPageDisplay";

/** Stable query-key factory for a single client's goals. */
export function clientGoalsKey(clientId: string | undefined) {
  return ["client-goals", clientId] as const;
}

async function fetchClientGoals(clientId: string): Promise<GoalListItem[]> {
  const res = await apiGet(`/api/clients/${clientId}/goals`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail ?? err.title ?? "Failed to load goals");
  }
  const json = await res.json();
  return json.data.goals as GoalListItem[];
}

/** Reads a single client's goals (enriched with read-time progress). */
export function useClientGoals(clientId: string | undefined) {
  return useQuery<GoalListItem[]>({
    queryKey: clientGoalsKey(clientId),
    queryFn: () => fetchClientGoals(clientId as string),
    enabled: !!clientId,
  });
}

/** Invalidates BOTH the per-client card key and the tenant-wide Goals page. */
function useGoalInvalidate() {
  const queryClient = useQueryClient();
  return (clientId: string) => {
    queryClient.invalidateQueries({ queryKey: clientGoalsKey(clientId) });
    queryClient.invalidateQueries({ queryKey: ["goals"] });
  };
}

/** The create/edit payload (matches the 18.3 route's Zod grammar). */
export interface GoalMutationPayload {
  name: string;
  goalType: GoalListItem["goalType"];
  metricKey?: string;
  platform?: string;
  targetValue: number;
  currentValue?: number;
  unit: GoalListItem["unit"];
  currencyCode?: string;
  periodType: GoalListItem["periodType"];
  periodStart?: number;
  periodEnd?: number;
  thresholdWarning?: number;
  thresholdDanger?: number;
}

async function readErrorOr(res: Response): Promise<unknown> {
  const json = await res
    .json()
    .catch(() => ({ detail: "Server error. Please try again." }));
  if (!res.ok) throw { status: res.status, ...(json as object) };
  return json;
}

export function useCreateClientGoal(clientId: string | undefined) {
  const invalidate = useGoalInvalidate();
  return useMutation({
    mutationFn: async (payload: GoalMutationPayload) => {
      const res = await apiPost(`/api/clients/${clientId}/goals`, payload);
      const json = (await readErrorOr(res)) as { data: GoalListItem };
      return json.data;
    },
    onSuccess: () => clientId && invalidate(clientId),
  });
}

export interface UpdateClientGoalVars {
  goalId: string;
  patch: Partial<GoalMutationPayload> & { status?: GoalListItem["status"] };
}

export function useUpdateClientGoal(clientId: string | undefined) {
  const invalidate = useGoalInvalidate();
  return useMutation({
    mutationFn: async ({ goalId, patch }: UpdateClientGoalVars) => {
      const res = await apiPatch(
        `/api/clients/${clientId}/goals/${goalId}`,
        patch,
      );
      const json = (await readErrorOr(res)) as { data: GoalListItem };
      return json.data;
    },
    onSuccess: () => clientId && invalidate(clientId),
  });
}

export function useDeleteClientGoal(clientId: string | undefined) {
  const invalidate = useGoalInvalidate();
  return useMutation({
    mutationFn: async (goalId: string) => {
      const res = await apiDelete(`/api/clients/${clientId}/goals/${goalId}`);
      if (!res.ok && res.status !== 204) {
        await readErrorOr(res);
      }
      return true;
    },
    onSuccess: () => clientId && invalidate(clientId),
  });
}
